import * as THREE from 'three';
import { load as loadYaml } from 'js-yaml';
import createSensorLabel, { updateSensorLabel } from '../ui/labels.js';
import { sensorVertex } from '../shaders/sensor.vert.glsl.js';
import { sensorFragment } from '../shaders/sensor.frag.glsl.js';
import logger from '../util/logger.js';
import { getSensorColor } from '../util/colors.js';

export async function loadSensors(url = '/sensors.yaml') {
	const res = await fetch(url);
	if (!res.ok) throw new Error(`Failed to fetch sensors: ${res.status}`);
	const txt = await res.text();
	const doc = loadYaml(txt);
	if (Array.isArray(doc)) return { sensors: doc, model: null };
	if (doc && typeof doc === 'object') {
		const sensors = Array.isArray(doc.sensors) ? doc.sensors : [];
		const model = typeof doc.model === 'string' ? doc.model : null;
		return { sensors, model };
	}
	return { sensors: [], model: null };
}

export function createSensorObject(sensor, opts = {}) {
	const radius = typeof opts.radius === 'number' ? opts.radius : 2;
	let rawColor;
	if (opts && typeof opts.color !== 'undefined') rawColor = opts.color;
	else if (sensor && typeof sensor.color !== 'undefined') rawColor = sensor.color;
	else rawColor = getSensorColor(sensor) || 0xff0000;
	const colorObj = new THREE.Color(rawColor);
	const colorHex = '#' + colorObj.getHexString();

	const geom = new THREE.SphereGeometry(radius, 24, 16);

	const mat = new THREE.ShaderMaterial({
		vertexShader: sensorVertex,
		fragmentShader: sensorFragment,
		uniforms: {
			uColor: { value: new THREE.Color(colorObj) },
			tDepth: { value: null },
			cameraNear: { value: 0.1 },
			cameraFar: { value: 10000.0 },
			resolution: { value: new THREE.Vector2(512, 512) },
			uHatchScale: { value: 30.0 },
			uHatchColor: { value: new THREE.Color(0x888888) },
			uHatchStrength: { value: 0.6 },
			uGlowColor: { value: new THREE.Color(0x888888) },
			uGlowStrength: { value: 2.4 }
		},
		transparent: true,
		depthTest: false,
		fog: false
	});
	mat.depthWrite = false;
	const mesh = new THREE.Mesh(geom, mat);
	mesh.renderOrder = 9999;
	mesh.frustumCulled = false;
	mesh.userData.sensor = sensor;

	// Determine a stable sensor UID. Prefer an existing server-provided
	// `_sensorUid` or `nodeid`/`id` fields; otherwise fall back to random UUID.
	// We compute a small deterministic hash (FNV-1a 32-bit) of the nodeid
	// so the UID remains stable across reloads when nodeid is stable.
	let uid = null;
	try {
		// prefer an explicitly provided UID
		if (sensor && sensor._sensorUid) {
			uid = String(sensor._sensorUid);
		} else {
			const stableKey = sensor && (sensor.nodeid || sensor.id || sensor.clientKey || sensor.label) ? String(sensor.nodeid || sensor.id || sensor.clientKey || sensor.label) : null;
			if (stableKey) {
				// FNV-1a 32-bit hash -> hex
				function fnv1a32(str) {
					let h = 0x811c9dc5;
					for (let i = 0; i < str.length; i++) {
						h ^= str.charCodeAt(i);
						// multiply by FNV prime (32-bit)
						h = (h >>> 0) * 0x01000193;
						h = h >>> 0;
					}
					return ('00000000' + (h >>> 0).toString(16)).slice(-8);
				}
				uid = `sensor-${fnv1a32(stableKey)}`;
			} else {
				if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') uid = crypto.randomUUID();
			}
		}
	} catch (err) { logger.warn && logger.warn('sensors: uid generation failed', err); }
	if (!uid) uid = `sensor-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
	mesh.userData._sensorUid = uid;

	// expose uid on original sensor object so viewers receiving sensor callbacks
	// can reference the DOM label via sensor._sensorUid
	try { if (sensor && typeof sensor === 'object') sensor._sensorUid = uid; } catch (e) { /* ignore */ }
	mesh.castShadow = false;
	mesh.receiveShadow = false;

	if (typeof sensor.x === 'number') mesh.position.set(sensor.x, sensor.y || 0, sensor.z || 0);

	const { labelEl, labelObj } = createSensorLabel(sensor, colorHex, { uid });
	mesh.add(labelObj);
	mesh.userData._labelEl = labelEl;
	mesh.userData._labelObj = labelObj;

	// hook to allow incremental label updates without recreating DOM
	mesh.userData._updateLabel = function (s) {
		try {
			updateSensorLabel(mesh.userData._labelEl, s || mesh.userData.sensor);
		} catch (e) { /* ignore update errors */ }
	};

	if (typeof createSensorLabel._attachDomHover === 'function') createSensorLabel._attachDomHover(labelEl);

	return mesh;
}

export default { loadSensors, createSensorObject };
